/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Dimmer, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { useProduitDomaineStore } from '@/zustand';
import getProduitDomaine from '@/routes/produitDomaine';
import type { Domaine } from '@/types/Domaine';
import type { ProduitDomaine } from '@/types/ProduitDomaine';
import type { Produit } from '@/types/Produit';

interface ProduitDomaineZustandProviderProps {
  children: React.ReactNode;
}
interface ResponseType {
  produitDomaine: ProduitDomaine;
  produitDomaineEtcv: ProduitDomaine;
  domaine: Domaine;
  produit: Produit;
}

const ProduitDomaineZustandProvider = (
  props: ProduitDomaineZustandProviderProps,
) => {
  const { children } = props;

  const setProduitDomaine = useProduitDomaineStore(
    (state) => state.setProduitDomaine,
  );
  const produitDomaine = useProduitDomaineStore(
    (state) => state.produitDomaine,
  );
  const setProduitDomaineEtcv = useProduitDomaineStore(
    (state) => state.setProduitDomaineEtcv,
  );
  const produitDomaineEtcv = useProduitDomaineStore(
    (state) => state.produitDomaineEtcv,
  );
  const setDomaine = useProduitDomaineStore((state) => state.setDomaine);
  const domaine = useProduitDomaineStore((state) => state.domaine);
  const setProduit = useProduitDomaineStore((state) => state.setProduit);
  const produit = useProduitDomaineStore((state) => state.produit);

  React.useEffect(() => {
    if (_.isNil(produitDomaine)) {
      request
        .get<unknown, ResponseType>(getProduitDomaine())
        .then((response: ResponseType) => {
          const {
            produitDomaine: pD,
            produitDomaineEtcv: pDEtcv,
            domaine: d,
            produit: p,
          } = response;
          setProduitDomaine(pD);
          setProduitDomaineEtcv(pDEtcv);
          setDomaine(d);
          setProduit(p);
        });
    }
  }, [
    produitDomaine,
    setDomaine,
    setProduit,
    setProduitDomaine,
    setProduitDomaineEtcv,
  ]);

  // Ajoute le header X-Produit-Domaine aux requêtes
  React.useEffect(() => {
    const xProduitDomaine = produitDomaine?.identifiant;
    const xProduitDomaineEtcv = produitDomaineEtcv?.identifiant;
    const xDomaine = domaine?.identifiant;
    const xProduit = produit?.identifiant;

    const interceptor = request.interceptors.request.use((config) => {
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Produit-Domaine'] = xProduitDomaine;
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Produit-Domaine-Etcv'] = xProduitDomaineEtcv;
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Domaine'] = xDomaine;
      // eslint-disable-next-line no-param-reassign
      config.headers['X-Produit'] = xProduit;

      return config;
    });

    return () => {
      request.interceptors.request.eject(interceptor);
    };
  }, [produitDomaine, domaine, produit, produitDomaineEtcv?.identifiant]);

  return (
    <>
      {children}
      <Dimmer active={_.isNil(produit)} inverted>
        <Loader indeterminate />
      </Dimmer>
    </>
  );
};

export default ProduitDomaineZustandProvider;
